import { useEffect, useState, useRef, useCallback } from "react";
import api from "../lib/api";
import { money, fmtDate, TREATMENTS } from "../lib/format";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Upload, Link2, CheckCircle2, Trash2, Landmark, Unlink, Sparkles, CheckCheck, FileUp } from "lucide-react";

export default function BankFeedsPage() {
  const [bank, setBank] = useState([]);
  const [categories, setCategories] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [importAccount, setImportAccount] = useState("");
  const [loading, setLoading] = useState(true);
  const [reconciling, setReconciling] = useState(null);
  const [category, setCategory] = useState("");
  const [treatment, setTreatment] = useState("standard");
  const [suggestions, setSuggestions] = useState({});
  const fileRef = useRef();
  const qifRef = useRef();

  const load = useCallback(async () => {
    setLoading(true);
    const [b, c, a] = await Promise.all([api.get("/bank/transactions"), api.get("/categories"), api.get("/bank-accounts")]);
    setBank(b.data);
    setCategories(c.data);
    setAccounts(a.data);
    setLoading(false);
    const unrec = b.data.filter((x) => !x.reconciled);
    const sugg = {};
    await Promise.all(unrec.map(async (row) => {
      try {
        const r = await api.get(`/bank/suggestions/${row.id}`);
        if (r.data.suggestions?.length) sugg[row.id] = r.data.suggestions[0];
      } catch { /* ignore */ }
    }));
    setSuggestions(sugg);
  }, []);

  useEffect(() => { load(); }, [load]);

  const upload = async (e, endpoint) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    if (importAccount) fd.append("account_id", importAccount);
    try {
      const { data } = await api.post(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.imported} transactions`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not parse the file.");
    }
    e.target.value = "";
  };

  const clearAll = async () => {
    const { data } = await api.post("/bank/clear-all");
    toast.success(`Cleared ${data.cleared} bank lines`);
    load();
  };

  const openReconcile = (b) => {
    setReconciling(b);
    setTreatment("standard");
    const type = b.amount >= 0 ? "income" : "expense";
    const firstCat = categories.find((c) => c.type === type);
    setCategory(firstCat?.name || "");
  };

  const confirmReconcile = async () => {
    const cat = categories.find((c) => c.name === category);
    await api.post(`/bank/reconcile/${reconciling.id}`, {
      create: true, category, treatment: cat?.treatment || treatment,
      party: reconciling.description,
    });
    toast.success("Reconciled & transaction created");
    setReconciling(null);
    load();
  };

  const unreconcile = async (id) => { await api.post(`/bank/unreconcile/${id}`); load(); };
  const remove = async (id) => { await api.delete(`/bank/transactions/${id}`); toast.success("Removed"); load(); };

  const matchExisting = async (bankId, transactionId) => {
    await api.post(`/bank/reconcile/${bankId}`, { create: false, transaction_id: transactionId });
    toast.success("Matched to existing transaction");
    load();
  };

  const unrec = bank.filter((b) => !b.reconciled).length;
  const recType = reconciling ? (reconciling.amount >= 0 ? "income" : "expense") : "income";

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Bank Feeds</h1>
          <p className="text-muted-foreground text-sm mt-1">Import a bank statement CSV, then match each line to your books.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {accounts.length > 0 && (
            <Select value={importAccount || "none"} onValueChange={(v) => setImportAccount(v === "none" ? "" : v)}>
              <SelectTrigger className="w-[170px]" data-testid="import-account-select"><SelectValue placeholder="No account" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No account</SelectItem>
                {accounts.map((a) => (<SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>))}
              </SelectContent>
            </Select>
          )}
          <input ref={fileRef} type="file" accept=".csv" onChange={(e) => upload(e, "/bank/import")} className="hidden" data-testid="csv-file-input" />
          <input ref={qifRef} type="file" accept=".qif" onChange={(e) => upload(e, "/bank/import-qif")} className="hidden" data-testid="qif-file-input" />
          <Button onClick={() => fileRef.current?.click()} data-testid="import-csv-btn" className="gap-2">
            <Upload className="w-4 h-4" /> Import CSV
          </Button>
          <Button variant="outline" onClick={() => qifRef.current?.click()} data-testid="import-qif-btn" className="gap-2">
            <FileUp className="w-4 h-4" /> Import QIF
          </Button>
          {bank.some((b) => !b.reconciled) && (
            <Button variant="outline" onClick={clearAll} data-testid="clear-all-btn" className="gap-2">
              <CheckCheck className="w-4 h-4" /> Clear all
            </Button>
          )}
        </div>
      </div>

      <div className="rounded-xl border border-dashed border-border bg-secondary/30 p-4 mb-6 flex items-center gap-3 text-sm text-muted-foreground">
        <Landmark className="w-5 h-5 shrink-0" />
        Supports CBA, ANZ, NAB &amp; Westpac exports (CSV or QIF). Expected CSV columns: <span className="font-mono text-foreground">Date, Description, Amount</span> (or Debit / Credit). {unrec} lines awaiting reconciliation.
      </div>

      <div className="rounded-2xl border border-border bg-card divide-y divide-border overflow-hidden">
        {loading ? <div className="p-10 text-center text-muted-foreground">Loading…</div> :
          bank.length === 0 ? <div className="p-12 text-center text-muted-foreground">No bank transactions. Import a CSV to begin.</div> :
          bank.map((b) => (
            <div key={b.id} className="flex items-center gap-4 px-5 py-3.5 row-hover" data-testid={`bank-row-${b.id}`}>
              <div className={`w-10 h-10 rounded-lg grid place-items-center shrink-0 ${b.reconciled ? "bg-emerald-50 text-emerald-600" : "bg-secondary text-muted-foreground"}`}>
                {b.reconciled ? <CheckCircle2 className="w-5 h-5" /> : <Landmark className="w-5 h-5" />}
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-medium text-sm truncate">{b.description}</div>
                <div className="text-xs text-muted-foreground num">{fmtDate(b.date)} · {b.bank}</div>
                {!b.reconciled && suggestions[b.id] && (
                  <button onClick={() => matchExisting(b.id, suggestions[b.id].id)} data-testid={`suggest-match-${b.id}`}
                    className="mt-1.5 inline-flex items-center gap-1.5 text-xs rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 hover:bg-emerald-100 transition-colors">
                    <Sparkles className="w-3 h-3" /> Match: {suggestions[b.id].description} · {money(suggestions[b.id].amount)}{suggestions[b.id].exact ? " (exact)" : ""}
                  </button>
                )}
              </div>
              <div className={`num font-semibold text-sm whitespace-nowrap ${b.amount >= 0 ? "text-emerald-600" : "text-foreground"}`}>
                {money(b.amount, true)}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {b.reconciled ? (
                  <button onClick={() => unreconcile(b.id)} data-testid={`unreconcile-${b.id}`}
                    className="p-2 rounded-md hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors" title="Unmatch">
                    <Unlink className="w-4 h-4" />
                  </button>
                ) : (
                  <Button size="sm" variant="outline" onClick={() => openReconcile(b)} data-testid={`reconcile-${b.id}`} className="gap-1.5 h-8">
                    <Link2 className="w-3.5 h-3.5" /> Match
                  </Button>
                )}
                <button onClick={() => remove(b.id)} data-testid={`delete-bank-${b.id}`}
                  className="p-2 rounded-md hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
      </div>

      <Dialog open={!!reconciling} onOpenChange={(v) => !v && setReconciling(null)}>
        <DialogContent className="sm:max-w-md" data-testid="reconcile-dialog">
          <DialogHeader><DialogTitle className="font-heading">Match bank transaction</DialogTitle></DialogHeader>
          {reconciling && (
            <div className="space-y-4 py-1">
              <div className="rounded-lg bg-secondary px-4 py-3">
                <div className="font-medium text-sm">{reconciling.description}</div>
                <div className="text-xs text-muted-foreground num mt-0.5">{fmtDate(reconciling.date)} · {money(reconciling.amount, true)}</div>
              </div>
              <div className="text-sm text-muted-foreground">
                This will create a new <span className="font-semibold text-foreground">{recType}</span> record for {money(Math.abs(reconciling.amount))} (incl. GST) and mark the line reconciled.
              </div>
              <div>
                <Label>Category</Label>
                <Select value={category} onValueChange={(v) => {
                  setCategory(v);
                  const c = categories.find((x) => x.name === v);
                  if (c) setTreatment(c.treatment);
                }}>
                  <SelectTrigger className="mt-1.5" data-testid="reconcile-category"><SelectValue placeholder="Select category" /></SelectTrigger>
                  <SelectContent>
                    {categories.filter((c) => c.type === recType).map((c) => (
                      <SelectItem key={c.id} value={c.name}>{c.name} · {TREATMENTS[c.treatment].short}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setReconciling(null)}>Cancel</Button>
            <Button onClick={confirmReconcile} data-testid="confirm-reconcile-btn">Match & create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
